import React, { useEffect, useState } from "react";
import io from "socket.io-client";
import { API_BASE } from "../../config/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Box, Typography, Modal, Fade, Paper, Divider, IconButton, Chip, Button, TextField, InputAdornment, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import defaultImg from "../../assets/img/header/img-2.png";
import { calculateCombinedReservationDuration } from "../../utils/bookingDuration";
import { encodeBookingGroups } from "../../utils/bookingPlan";
import { useCart } from "../../context/CartContext";
import { SOCKET_URL } from "../../config/api";

const CATEGORY_DISPLAY_ORDER = [
  "SALON",
  "BARBERÍA",
  "PESTAÑAS",
  "UÑAS",
  "DÍA ESPECIAL",
];

const normalizeCategoryName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const cleanCatalogValues = (value) => {
  if (typeof value === "string") {
    const text = value.trim();
    return /^(undefined|null|nan)$/i.test(text) ? "" : text;
  }
  if (Array.isArray(value)) return value.map(cleanCatalogValues);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cleanCatalogValues(item)])
    );
  }
  return value;
};

const getCategoryOrderIndex = (categoryName = "") => {
  const normalizedName = normalizeCategoryName(categoryName);
  const index = CATEGORY_DISPLAY_ORDER.findIndex(
    (item) => normalizeCategoryName(item) === normalizedName
  );

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const modalPaperSx = {
  width: "100%",
  maxWidth: 620,
  maxHeight: {
    xs: "100dvh",
    sm: "90vh",
  },
  height: {
    xs: "100dvh",
    sm: "auto",
  },
  borderRadius: {
    xs: 0,
    sm: 4,
  },
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  bgcolor: "#fff",
  boxShadow: "0 28px 90px rgba(45, 35, 18, 0.25)",
  "& > .MuiBox-root:first-of-type": {
    backgroundColor: "#fcfaf6",
    borderBottomColor: "#ebe4d7",
    gap: 1,
    "& .MuiIconButton-root": { flexShrink: 0, backgroundColor: "#fff", border: "1px solid #e9e1d2", width: 36, height: 36 },
    "& .MuiTypography-h5": { fontSize: { xs: 18, sm: 21 }, lineHeight: 1.3, color: "#30291e" },
  },
};

function SelectionModal({ open, children, ...props }) {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  return (
    <Modal
      {...props}
      open={open}
      closeAfterTransition
      slotProps={{ backdrop: { invisible: true } }}
      sx={{
        "& .MuiPaper-root": {
          animation: reducedMotion ? "none" : "selectionStepEnter 280ms cubic-bezier(0.22, 1, 0.36, 1) both",
          "@keyframes selectionStepEnter": {
            from: { opacity: 0, transform: "translateY(16px) scale(0.98)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
        },
      }}
    >
      <Fade in={open} timeout={reducedMotion ? 0 : { enter: 220, exit: 160 }}>
        {children}
      </Fade>
    </Modal>
  );
}

const modalContentSx = {
  flex: 1,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
  p: {
    xs: 2,
    sm: 3,
  },
};

function SelectionGuide({ step, title, description, context }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", gap: 0.75, mb: 2 }}>
        {["Servicio", "Profesional", "Resumen"].map((label, index) => (
          <Box key={label} sx={{ flex: 1, borderTop: "3px solid", borderColor: index <= step ? "#8a6d3b" : "#eee9df", pt: 0.75 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: index <= step ? "#8a6d3b" : "#918b80" }}>{index + 1}. {label}</Typography>
          </Box>
        ))}
      </Box>
      {context && <Typography sx={{ fontSize: 11, color: "#8a6d3b", mb: 0.75, overflowWrap: "anywhere" }}>{context}</Typography>}
      <Typography component="h3" sx={{ color: "#30291e", fontSize: 20, fontWeight: 800, mb: 0.75 }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: "#7a7469", lineHeight: 1.7 }}>{description}</Typography>
    </Box>
  );
}

const StyleCutting = () => {
  const navigate = useNavigate();
  const { addBookingGroups } = useCart();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [openServiceModal, setOpenServiceModal] = useState(false);
  const [openEmployeeModal, setOpenEmployeeModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState("replace");
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);

  // 👉 NUEVO
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [bookingGroups, setBookingGroups] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceQuery, setServiceQuery] = useState("");
  const currentGroup = bookingGroups[activeGroupIndex] || { id: "group-1", assignments: [] };
  const serviceAssignments = currentGroup.assignments || [];
  const allAssignments = bookingGroups.flatMap((group) => group.assignments || []);
  const totalReservationAmount = allAssignments.reduce(
    (total, assignment) => total + Number(assignment.service?.price || 0),
    0
  );
  const totalDuration = bookingGroups.reduce((total, group) => {
    const groupAssignments = group.assignments || [];
    const groupServices = groupAssignments.map((assignment) => ({
      ...assignment.service,
      categoryId: assignment.categoryId ?? assignment.service?.categoryId,
    }));

    return (
      total +
      calculateCombinedReservationDuration(groupServices, groupAssignments)
    );
  }, 0);
  const canAddMoreServices =
    serviceAssignments.length > 0 &&
    serviceAssignments.every((assignment) => assignment.service?.combinable);
  const hasBookingGroups = bookingGroups.some((group) => (group.assignments || []).length > 0);
  const bookingGroupSummaries = bookingGroups
    .map((group, index) => ({
      id: group.id || `group-${index + 1}`,
      assignments: group.assignments || [],
      index,
    }))
    .filter((group) => group.assignments.length > 0);

  // ===============================
  // Fetch categorías
  // ===============================
  const getCategories = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/category`
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Error cargando categorías");
      }

      const sortedCategories = cleanCatalogValues(Array.isArray(data) ? data : []).sort((a, b) => {
        const orderDiff =
          getCategoryOrderIndex(a?.name) - getCategoryOrderIndex(b?.name);

        if (orderDiff !== 0) return orderDiff;

        return (a?.name || "").localeCompare(b?.name || "");
      });

      setCategories(sortedCategories);
    } catch (error) {
      console.error(error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleCatalogUpdate = () => {
      getCategories();
    };

    const handleEmployeesUpdate = () => {
      getCategories();
    };

    socket.on("catalog:updated", handleCatalogUpdate);
    socket.on("employees:updated", handleEmployeesUpdate);

    return () => {
      socket.off("catalog:updated", handleCatalogUpdate);
      socket.off("employees:updated", handleEmployeesUpdate);
      socket.disconnect();
    };
  }, []);

  // ===============================
  // Handlers
  // ===============================
  const startReservationFlow = (cat) => {
    setBookingGroups([]);
    setActiveGroupIndex(0);
    setSelectionMode("replace");
    setSelectedCategory(cat);
    setOpenCategoryModal(true);
  };

  const openServices = (sub) => {
    setServiceQuery("");
    setSelectedSubCategory(sub);
    setOpenCategoryModal(false);
    setOpenServiceModal(true);
  };

  const openEmployees = (service) => {
    getCategories();
    setSelectedService(service);
    setOpenServiceModal(false);
    setOpenEmployeeModal(true);
  };

  const handleServiceClick = (service) => {
    if (selectionMode === "combine" && !service.combinable) {
      return;
    }

    openEmployees(service);
  };

  const handleEmployeeSelect = (employee) => {
    const nextAssignment = {
      service: selectedService,
      employee,
      categoryId: selectedCategory?.id ?? null,
    };
    const targetGroupIndex =
      selectionMode === "new_group" ? bookingGroups.length : activeGroupIndex;

    setBookingGroups((currentGroups) => {
      const nextGroups = [...currentGroups];
      const currentGroupData = nextGroups[targetGroupIndex] || {
        id: `group-${targetGroupIndex + 1}`,
        assignments: [],
      };

      nextGroups[targetGroupIndex] = {
        ...currentGroupData,
        assignments:
          selectionMode === "combine"
            ? [...currentGroupData.assignments, nextAssignment]
            : [nextAssignment],
      };

      return nextGroups;
    });

    setActiveGroupIndex(targetGroupIndex);
    setOpenEmployeeModal(false);
    setOpenCategoryModal(false);
    setOpenServiceModal(false);
    setOpenInfoModal(true);
    setSelectionMode("replace");
  };

  const goBackFromCategoryModal = () => {
    if (selectionMode !== "replace" && selectedCategory) {
      setSelectedCategory(null);
      return;
    }
    closeAll();
  };

  const goBackFromServiceModal = () => {
    setOpenServiceModal(false);
    setSelectedSubCategory(null);
    setOpenCategoryModal(true);
  };

  const goBackFromEmployeeModal = () => {
    setOpenEmployeeModal(false);
    setOpenServiceModal(true);
  };

  const goBackFromInfoModal = () => {
    setOpenInfoModal(false);
    setOpenEmployeeModal(true);
  };

  const closeSelectionModals = () => {
    setOpenEmployeeModal(false);
    setOpenServiceModal(false);
    setOpenCategoryModal(false);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedService(null);

    if (hasBookingGroups) {
      setOpenInfoModal(true);
    } else {
      setSelectionMode("replace");
    }
  };

  const closeAll = () => {
    setOpenEmployeeModal(false);
    setOpenServiceModal(false);
    setOpenCategoryModal(false);
    setOpenInfoModal(false);
    setSelectionMode("replace");
    setActiveGroupIndex(0);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedService(null);
    setBookingGroups([]);
  };

  const handleSaveCart = () => {
    if (!bookingGroupSummaries.length) {
      return;
    }

    addBookingGroups(bookingGroups);
    closeAll();
    navigate("/carrito");
  };

  if (loading) return <Typography sx={{ py: 8, textAlign: "center", color: "#786d59" }}>Cargando nuestros servicios...</Typography>;

  return (
    <>
      {/* ===================== */}
      {/* CATEGORÍAS */}
      {/* ===================== */}
      <Box component="section" id="servicios" sx={{ py: { xs: 6, md: 9 }, px: { xs: 2, sm: 3 }, textAlign: "center", background: "linear-gradient(180deg, #fff 0%, #fcf8f0 100%)" }}>
        <Typography sx={{ color: "#8a6d3b", fontSize: 11, fontWeight: 800, letterSpacing: 2, mb: 1.5 }}>ESTUDIO ISAAC · SERVICIOS</Typography>
        <Typography variant="h4" sx={{ color: "#30291e", fontWeight: 800, fontSize: { xs: 29, md: 40 }, letterSpacing: -1, mb: 1.5 }}>
          ¿Qué te gustaría hacerte?
        </Typography>
        <Typography sx={{ color: "#7b7468", fontSize: { xs: 14, md: 16 }, maxWidth: 540, mx: "auto", lineHeight: 1.7, mb: 3 }}>Elegí una categoría para encontrar tu servicio. Después seleccioná a tu profesional y el horario de tu visita.</Typography>
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: { xs: 1.5, sm: 3 }, mb: 4 }}>
          {["Elegí tu servicio", "Elegí tu profesional", "Reservá fecha y hora"].map((label, index) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 24, height: 24, display: "grid", placeItems: "center", borderRadius: "50%", backgroundColor: "#f5ecd9", color: "#8a6d3b", fontSize: 11, fontWeight: 800 }}>{index + 1}</Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#786b56" }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 3,
            maxWidth: 1180,
            mx: "auto",
          }}
        >
          {categories.map((cat) => (
            <Box
              component="button"
              type="button"
              aria-label={`Ver servicios de ${cat.name}`}
              key={cat.id}
              onClick={() => startReservationFlow(cat)}
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 4,
                border: "1px solid #ebe4d7",
                p: 0,
                backgroundColor: "#fff",
                textAlign: "left",
                fontFamily: "inherit",
                boxShadow: "0 8px 26px rgba(80, 61, 29, 0.06)",
                transition: "transform 180ms, box-shadow 180ms",
                "&:hover": { transform: "translateY(-5px)", boxShadow: "0 15px 35px rgba(80, 61, 29, 0.12)", "& img": { transform: "scale(1.04)" } },
                "&:focus-visible": { outline: "3px solid #b99a61", outlineOffset: 4 },
                cursor: "pointer",
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 45%",
                  md: "1 1 30%",
                },
                maxWidth: { md: "30%" },
              }}
            >
              <Box
                component="img"
                src={cat.image || defaultImg}
                alt={cat.name}
                sx={{
                  width: "100%",
                  height: { xs: 190, sm: 220, md: 260 },
                  display: "block",
                  objectFit: "cover",
                  borderRadius: 0,
                  transition: "transform 0.5s ease",
                  "&:hover": { transform: "scale(1.08)" },
                }}
              />

              <Box
                sx={{
                  p: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography component="span" sx={{ display: "block", color: "#30291e", fontWeight: 800, fontSize: 18, overflowWrap: "anywhere" }}>{cat.name}</Typography>
                  <Typography component="span" sx={{ display: "block", mt: 0.75, fontSize: 12, color: "#867b68" }}>Explorá y elegí tu servicio</Typography>
                </Box>
                <Box component="span" sx={{ display: "grid", placeItems: "center", width: 36, height: 36, borderRadius: "50%", backgroundColor: "#f6edda", color: "#8a6d3b", flexShrink: 0 }}><ArrowForwardRoundedIcon fontSize="small" /></Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ===================== */}
      {/* SUBCATEGORÍAS */}
      {/* ===================== */}
   <SelectionModal
  open={openCategoryModal}
  onClose={hasBookingGroups ? closeSelectionModals : closeAll}
  closeAfterTransition
>
  <Box
    onClick={hasBookingGroups ? closeSelectionModals : closeAll}
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100dvh",
      p: { xs: 0, sm: 2 },
      bgcolor: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
    }}
  >
    <Paper
      elevation={6}
      onClick={(e) => e.stopPropagation()}
      sx={modalPaperSx}
    >
      {/* HEADER FIJO */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: "1px solid #ececec",
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={goBackFromCategoryModal}
            sx={{
              bgcolor: "#f5f5f5",
              "&:hover": {
                bgcolor: "#e0e0e0",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
              },
            }}
          >
            {selectedCategory?.name || "Elegir categoría"}
          </Typography>
        </Box>

        {/* BOTÓN CERRAR */}
        <IconButton
          onClick={hasBookingGroups ? closeSelectionModals : closeAll}
          sx={{
            bgcolor: "#f5f5f5",
            "&:hover": {
              bgcolor: "#e0e0e0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENIDO SCROLLEABLE */}
      <Box sx={modalContentSx}>
        <SelectionGuide step={0} title={selectedCategory ? "¿Qué tipo de servicio buscás?" : "Elegí una categoría"} description="Seleccioná una opción para ver los servicios, su duración y el importe de reserva." context={selectedCategory?.name} />
        {selectionMode === "combine" && !selectedCategory && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 3,
              bgcolor: "#fcf6e8",
              border: "1px solid #ead9b4",
            }}
          >
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
              Podés combinar servicios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Agregá otro servicio a esta visita. Al terminar, te mostraremos
              la duración y el importe total de la reserva.
            </Typography>
          </Box>
        )}

        {selectionMode === "combine" && !selectedCategory ? (
          categories.map((cat) => (
            <Box
              key={cat.id}
              onClick={() => setSelectedCategory(cat)}
              sx={{
                p: {
                  xs: 2,
                  sm: 2.5,
                },
                mb: 2,
                borderRadius: 3,
                cursor: "pointer",
                border: "1px solid #ececec",
                bgcolor: "#fff",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  component="img"
                  src={cat.image || defaultImg}
                  alt={cat.name}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                />

                <Box>
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: {
                      xs: 15,
                      sm: 17,
                    },
                  }}
                >
                  {cat.name}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    fontSize: 13,
                  }}
                >
                  Elegir categoría
                </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: 22,
                  color: "#9e9e9e",
                  fontWeight: "bold",
                }}
              >
                ›
              </Typography>
            </Box>
          ))
        ) : (
          selectedCategory?.SubCategories?.map((sub) => (
            <Box
              key={sub.id}
              onClick={() => openServices(sub)}
              sx={{
                p: {
                  xs: 2,
                  sm: 2.5,
                },
                mb: 2,
                borderRadius: 3,
                cursor: "pointer",
                border: "1px solid #ececec",
                bgcolor: "#fff",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Box>
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: {
                      xs: 15,
                      sm: 17,
                    },
                  }}
                >
                  {sub.name}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    fontSize: 13,
                  }}
                >
                  Ver servicios disponibles
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: 22,
                  color: "#9e9e9e",
                  fontWeight: "bold",
                }}
              >
                ›
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Paper>
  </Box>
</SelectionModal>

    <SelectionModal
  open={openServiceModal}
  onClose={closeSelectionModals}
  closeAfterTransition
>
  <Box
    onClick={closeSelectionModals}
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100dvh",
      p: { xs: 0, sm: 2 },
      bgcolor: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
    }}
  >
    <Paper
      elevation={6}
      onClick={(e) => e.stopPropagation()}
      sx={modalPaperSx}
    >
      {/* HEADER FIJO */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: "1px solid #ececec",
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={goBackFromServiceModal}
            sx={{
              bgcolor: "#f5f5f5",
              "&:hover": {
                bgcolor: "#e0e0e0",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
              },
            }}
          >
            {selectedSubCategory?.name}
          </Typography>
        </Box>

        {/* BOTÓN CERRAR */}
        <IconButton
          onClick={closeSelectionModals}
          sx={{
            bgcolor: "#f5f5f5",
            "&:hover": {
              bgcolor: "#e0e0e0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENIDO SCROLLEABLE */}
      <Box sx={modalContentSx}>
        <SelectionGuide step={0} title="Elegí tu servicio" description="Revisá los detalles y seleccioná el servicio que querés reservar." context={`${selectedCategory?.name || ""} / ${selectedSubCategory?.name || ""}`} />
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar un servicio..."
          value={serviceQuery}
          onChange={(event) => setServiceQuery(event.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> } }}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5, backgroundColor: "#fcfaf6" } }}
          inputProps={{ "aria-label": "Buscar un servicio" }}
        />
        {!selectedSubCategory?.Services?.some((srv) => normalizeCategoryName(srv.name).includes(normalizeCategoryName(serviceQuery))) && (
          <Typography sx={{ py: 3, textAlign: "center", color: "#867b68", fontSize: 14 }}>No encontramos servicios. Probá con otro nombre o volvé para elegir otra opción.</Typography>
        )}
        {selectedSubCategory?.Services?.filter((srv) => normalizeCategoryName(srv.name).includes(normalizeCategoryName(serviceQuery))).map((srv) => (
          <Box
            key={srv.id}
            onClick={() => handleServiceClick(srv)}
            sx={{
              p: {
                xs: 2,
                sm: 2.5,
              },
              mb: 2,
              borderRadius: 3,
              cursor: "pointer",
              border:
                selectionMode === "combine" && !srv.combinable
                  ? "1px dashed #d9d9d9"
                  : "1px solid #ececec",
              bgcolor: "#fff",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              opacity: selectionMode === "combine" && !srv.combinable ? 0.55 : 1,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              },
            }}
            >
            {/* Nombre */}
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: {
                    xs: 16,
                    sm: 18,
                  },
                }}
              >
                {srv.name}
              </Typography>
              {srv.combinable && (
                <Chip
                  label="Podés sumar otro servicio"
                  size="small"
                  sx={{ bgcolor: "#f5ecd9", color: "#8a6d3b", fontSize: 10, flexShrink: 0 }}
                />
              )}
            </Box>

            {/* Descripción */}
            {srv.description &&
              srv.description !== "undefined" && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    lineHeight: 1.6,
                  }}
                >
                  {srv.description}
                </Typography>
              )}

            <Divider sx={{ my: 2 }} />

            {/* Info */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
  variant="body2"
  color="text.secondary"
>
  ⏱ Duración:{" "}
  {Number(srv.duration) > 0 ? (srv.duration >= 60
    ? `${Math.floor(srv.duration / 60)}h ${
        srv.duration % 60 !== 0
          ? `${srv.duration % 60}min`
          : ""
      }`
    : `${srv.duration} min`) : "A consultar"}
</Typography>

              {/* Reserva */}
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "#8a6d3b",
                }}
              >
                Importe para reservar: {srv.price !== null && srv.price !== undefined && srv.price !== "" && Number.isFinite(Number(srv.price)) ? `$${srv.price}` : "A consultar"}
              </Typography>

              {/* Precio total */}
               {srv.priceTotal && (

                 <Typography
                 sx={{
                   fontWeight: "bold",
                   color: "#8a6d3b",
                  }}
                  >
                Valor del servicio: ${srv.priceTotal}
              </Typography>
              )}

              {/* Mensaje */}
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#f5f5f5",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.5,
                    fontSize: 13,
                  }}
                >
                  El monto de la reservación será descontado
                  del precio total del servicio al momento de
                  abonar.
                </Typography>
              </Box>

              {/* Promo */}
              {typeof srv.promotion === "string" &&
                srv.promotion.trim() !== "" &&
                !/^(undefined|null|nan)$/i.test(srv.promotion.trim().replace(/^["']+|["']+$/g, "")) && (
                <Box
                  sx={{
                    mt: 1,
                    alignSelf: "flex-start",
                    bgcolor: "#ffebee",
                    color: "#d32f2f",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: 13,
                  }}
                >
                  🎉 {srv.promotion}
                </Box>
              )}

              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontWeight: "bold",
                  color: srv.combinable ? "#111" : "#616161",
                }}
              >
                {srv.combinable
                  ? "Podés agregar otro servicio antes de elegir el horario."
                  : selectionMode === "combine"
                    ? "No se puede agregar a una combinación"
                    : "Este servicio se agenda por separado."}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                disabled={selectionMode === "combine" && !srv.combinable}
                onClick={(event) => { event.stopPropagation(); handleServiceClick(srv); }}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{ mt: 1, py: 1.25, borderRadius: 2.5, backgroundColor: "#8a6d3b", textTransform: "none", fontWeight: 700, "&:hover": { backgroundColor: "#70572f" } }}
              >
                Elegir este servicio
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  </Box>
</SelectionModal>

      {/* ===================== */}
      {/* EMPLEADOS */}
      {/* ===================== */}
    <SelectionModal
  open={openEmployeeModal}
  onClose={closeSelectionModals}
  closeAfterTransition
>
  <Box
    onClick={closeSelectionModals}
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100dvh",
      p: { xs: 0, sm: 2 },
      bgcolor: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
    }}
  >
    <Paper
      elevation={6}
      onClick={(e) => e.stopPropagation()}
      sx={modalPaperSx}
    >
      {/* HEADER FIJO */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: "1px solid #ececec",
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              onClick={goBackFromEmployeeModal}
              sx={{
                bgcolor: "#f5f5f5",
                "&:hover": {
                  bgcolor: "#e0e0e0",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: {
                  xs: "1.2rem",
                  sm: "1.5rem",
                },
              }}
            >
              Elegí tu profesional
            </Typography>
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Seleccioná el especialista que prefieras
          </Typography>
        </Box>

        {/* BOTÓN CERRAR */}
        <IconButton
          onClick={closeSelectionModals}
          sx={{
            bgcolor: "#f5f5f5",
            "&:hover": {
              bgcolor: "#e0e0e0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENIDO SCROLLEABLE */}
      <Box sx={modalContentSx}>
        <SelectionGuide step={1} title="¿Con quién querés atenderte?" description="Seleccioná a tu profesional. En el siguiente paso podrás elegir la fecha y consultar sus horarios." context={selectedService?.name} />
        {selectedService?.employees?.filter((employee) => employee.active).length ? (
          selectedService.employees
            .filter((employee) => employee.active)
            .map((emp) => (
              <Box
                key={emp.id}
                onClick={() => {
                  handleEmployeeSelect(emp);
                }}
                sx={{
                  p: {
                    xs: 2,
                    sm: 2.5,
                  },
                  mb: 2,
                  borderRadius: 3,
                  cursor: "pointer",
                  border: "1px solid #ececec",
                  bgcolor: "#fff",
                  transition: "all 0.2s ease",
                  boxShadow:
                    "0 2px 10px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 6px 18px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      bgcolor: emp.background_color || "#8a6d3b",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {emp.name?.charAt(0)?.toUpperCase()}
                  </Box>

                  {/* Info */}
                  <Box>
                    <Typography
                      fontWeight="bold"
                      sx={{
                        fontSize: {
                          xs: 15,
                          sm: 17,
                        },
                      }}
                    >
                      {emp.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        fontSize: 13,
                      }}
                    >
                      Seleccionar profesional
                    </Typography>
                  </Box>
                </Box>

                {/* Flecha */}
                <Typography
                  sx={{
                    fontSize: 22,
                    color: "#9e9e9e",
                    fontWeight: "bold",
                  }}
                >
                  ›
                </Typography>
              </Box>
            ))
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              py: 6,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                No hay profesionales disponibles
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Elegí otro servicio o comunicate con el salón para consultar.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  </Box>
</SelectionModal>

      {/* ===================== */}
      {/* MODAL INFO PAGO RESERVA */}
      {/* ===================== */}
    <SelectionModal
  open={openInfoModal}
  onClose={() => setOpenInfoModal(false)}
  closeAfterTransition
>
  <Box
    onClick={() => setOpenInfoModal(false)}
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100dvh",
      p: { xs: 0, sm: 2 },
      bgcolor: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
    }}
  >
    <Paper
      elevation={6}
      onClick={(e) => e.stopPropagation()}
      sx={{
        ...modalPaperSx,
        borderRadius: {
          xs: 0,
          sm: 5,
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          borderBottom: "1px solid #ececec",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={goBackFromInfoModal}
            sx={{
              bgcolor: "#f5f5f5",
              "&:hover": {
                bgcolor: "#e0e0e0",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
              },
            }}
          >
            Tu selección
          </Typography>
        </Box>

        {/* BOTÓN CERRAR */}
        <IconButton
          onClick={() => setOpenInfoModal(false)}
          sx={{
            bgcolor: "#f5f5f5",
            "&:hover": {
              bgcolor: "#e0e0e0",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENIDO */}
      <Box
        sx={{
          ...modalContentSx,
        }}
      >
        <SelectionGuide step={2} title="Todo listo para elegir tu horario" description="Revisá tu selección. Todavía no reservaste: el próximo paso es elegir fecha y hora." />
        {/* ALERTA */}
          <Box
            sx={{
              mb: 3,
            p: 2,
            borderRadius: 3,
            bgcolor: "#fcf6e8",
            border: "1px solid #ead9b4",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              color: "#8a6d3b",
              mb: 1,
              fontSize: 15,
            }}
          >
            ¿Cómo funciona el importe de reserva?
          </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#5f6368",
                lineHeight: 1.7,
              }}
            >
              El importe indicado corresponde
              únicamente a la{" "}
              <strong>reserva del turno</strong>, no al
              valor total del servicio.
            </Typography>
            {serviceAssignments.length > 1 && (
              <Typography
                variant="body2"
                sx={{
                  color: "#5f6368",
                  lineHeight: 1.7,
                  mt: 1,
                }}
              >
                Si combinás categorías distintas, el horario se calcula con la
                duración más larga. Si combinás servicios de la misma categoría,
                las duraciones se suman.
              </Typography>
            )}
          </Box>

        {/* INFO */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            borderRadius: 3,
            bgcolor: "#f5f5f5",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.8,
              color: "#424242",
            }}
          >
            Una vez finalizado el servicio, el importe
            abonado en la reserva{" "}
            <strong>
              será descontado automáticamente
            </strong>{" "}
            del monto total a pagar.
          </Typography>
        </Box>

        {/* RESUMEN */}
        <Box
          sx={{
            mb: 4,
            borderRadius: 3,
            border: "1px solid #ececec",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #ececec",
              bgcolor: "#fafafa",
            }}
          >
            <Typography fontWeight="bold">
              Resumen de la reserva
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography color="text.secondary">
                Servicios
              </Typography>

              <Box sx={{ textAlign: "right" }}>
                {bookingGroups.map((group, groupIndex) => (
                    <Box key={group.id || `summary-services-${groupIndex}`} sx={{ mb: groupIndex === bookingGroups.length - 1 ? 0 : 1 }}>
                      <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
                        Servicio {groupIndex + 1}
                      </Typography>
                      {(group.assignments || []).map((assignment) => (
                        <Typography key={`${groupIndex}-${assignment.service.id}-${assignment.employee.id}`} fontWeight="bold">
                          {assignment.service.name} - {assignment.employee.name}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography color="text.secondary">
                Profesionales
              </Typography>

              <Box sx={{ textAlign: "right" }}>
                {bookingGroups.map((group, groupIndex) => (
                    <Box key={group.id || `summary-employees-${groupIndex}`} sx={{ mb: groupIndex === bookingGroups.length - 1 ? 0 : 1 }}>
                      <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
                        Servicio {groupIndex + 1}
                      </Typography>
                      {(group.assignments || []).map((assignment) => (
                        <Typography key={`employee-${groupIndex}-${assignment.service.id}-${assignment.employee.id}`} fontWeight="bold">
                          {assignment.employee.name}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography color="text.secondary">
                Reserva
              </Typography>

              <Typography
                fontWeight="bold"
                sx={{
                  color: "#ef6c00",
                }}
              >
                ${totalReservationAmount}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1.5,
              }}
            >
              <Typography color="text.secondary">
                Duración total
              </Typography>

              <Typography fontWeight="bold">
                {totalDuration} min
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* BOTONES */}
        <Box
          sx={{
            display: "grid",
            gap: 1.25,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            "& > button": { fontFamily: "inherit", fontSize: 13, lineHeight: 1.5, minHeight: 50, border: "1px solid transparent", "&:focus-visible": { outline: "3px solid #b99a61", outlineOffset: 2 } },
          }}
        >
          {/* CANCELAR */}
          <Box
            component="button"
            type="button"
            onClick={() => setOpenInfoModal(false)}
            sx={{
              flex: 1,
              order: {
                xs: 3,
                sm: 1,
              },
              p: 1.7,
              textAlign: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 3,
              cursor: "pointer",
              fontWeight: "bold",
              transition: "0.2s",
              "&:hover": {
                bgcolor: "#e0e0e0",
              },
            }}
          >
            Seguir explorando
          </Box>

          <Box
            component="button"
            type="button"
            disabled={!canAddMoreServices}
            onClick={() => {
              if (!canAddMoreServices) {
                return;
              }

              setSelectionMode("combine");
              setOpenInfoModal(false);
              setSelectedCategory(null);
              setSelectedSubCategory(null);
              setSelectedService(null);
              setOpenCategoryModal(true);
            }}
            sx={{
              flex: 1,
              order: 2,
              p: 1.7,
              textAlign: "center",
              bgcolor: "#fff",
              color: canAddMoreServices ? "#111" : "#9e9e9e",
              border: "1px solid #d9d9d9",
              borderRadius: 3,
              cursor: canAddMoreServices ? "pointer" : "not-allowed",
              fontWeight: "bold",
              transition: "0.2s",
              opacity: canAddMoreServices ? 1 : 0.65,
              "&:hover": {
                bgcolor: canAddMoreServices ? "#f5f5f5" : "#fff",
              },
            }}
          >
            Sumar otro servicio
          </Box>

          <Box
            component="button"
            type="button"
            onClick={handleSaveCart}
            sx={{
              flex: 1,
              order: 2,
              p: 1.7,
              textAlign: "center",
              bgcolor: "#fff",
              color: "#111",
              border: "1px solid #d9d9d9",
              borderRadius: 3,
              cursor: "pointer",
              fontWeight: "bold",
              transition: "0.2s",
              "&:hover": {
                bgcolor: "#f5f5f5",
              },
            }}
          >
            Guardar en el carrito
          </Box>

          {/* CONTINUAR */}
          <Box
            component="button"
            type="button"
            disabled={!bookingGroupSummaries.length}
            onClick={() =>
              navigate(
                `/reservar/${bookingGroups[0]?.assignments?.[0]?.service.id}/${bookingGroups[0]?.assignments?.[0]?.employee.id}?bookingGroups=${encodeBookingGroups(bookingGroups)}`
              )
            }
            sx={{
              flex: 1,
              order: {
                xs: 1,
                sm: 3,
              },
              p: 1.7,
              textAlign: "center",
              bgcolor: "#111",
              color: "#fff",
              borderRadius: 3,
              cursor: "pointer",
              fontWeight: "bold",
              transition: "0.2s",
              "&:hover": {
                bgcolor: "#000",
                transform: "translateY(-1px)",
              },
              opacity: bookingGroupSummaries.length ? 1 : 0.6,
              pointerEvents: bookingGroupSummaries.length ? "auto" : "none",
            }}
          >
            Elegir fecha y horario
          </Box>
        </Box>
      </Box>
    </Paper>
  </Box>
</SelectionModal>
    </>
  );
};

export default StyleCutting;
